import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Check, ChevronDown } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useQuery } from "@tanstack/react-query";
import { documentFormApi } from "@/services/api";
import { cn } from "@/lib/utils";
import { DocumentOutputField } from "@/models/DocumentOutputFormat";

interface DocumentFormFieldDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (fieldData: Partial<DocumentOutputField>) => Promise<void>;
  documentId: number;
  field?: DocumentOutputField | null; // If provided, it's edit mode
  isSubmitting?: boolean;
}

const DocumentFormFieldDialog = ({
  isOpen,
  onClose,
  onSave,
  documentId,
  field = null,
  isSubmitting = false,
}: DocumentFormFieldDialogProps) => {
  const { toast } = useToast();
  const [fieldTitle, setFieldTitle] = useState(""); // Changed from fieldLabel
  const [fieldName, setFieldName] = useState(""); // New field name state
  const [fieldValue, setFieldValue] = useState("");
  const [fieldComment, setFieldComment] = useState(""); // New comment state
  const [fieldValueSource, setFieldValueSource] = useState<
    "predefined" | "user_input"
  >("predefined");
  const [referencedFieldId, setReferencedFieldId] = useState<
    number | undefined
  >(undefined);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [isFieldNameManuallyEntered, setIsFieldNameManuallyEntered] =
    useState(false);

  const isEditMode = !!field;

  // Initialize form values when dialog opens or field changes
  useEffect(() => {
    if (isOpen) {
      if (field) {
        // Edit mode - populate with existing values
        setFieldTitle(field.title);
        setFieldName(field.name || ""); // Handle optional name
        setFieldValue(field.value || "");
        setFieldComment(field.comment || ""); // Handle optional comment
        setFieldValueSource(field.value_source);
        setReferencedFieldId(field.referenced_field_id);
        setIsFieldNameManuallyEntered(true); // In edit mode, assume name was set intentionally
      } else {
        // Add mode - reset to defaults
        setFieldTitle("");
        setFieldName("");
        setFieldValue("");
        setFieldComment("");
        setFieldValueSource("predefined");
        setReferencedFieldId(undefined);
        setIsFieldNameManuallyEntered(false);
      }
    }
  }, [isOpen, field]);

  // Auto-generate name from title when title changes (only if not manually entered)
  useEffect(() => {
    if (fieldTitle && !isFieldNameManuallyEntered && !isEditMode) {
      const autoGeneratedName = fieldTitle
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, "_")
        .trim();
      setFieldName(autoGeneratedName);
    }
  }, [fieldTitle, isFieldNameManuallyEntered, isEditMode]);

  // Fetch form data to get available fields for user_input option
  const { data: formData } = useQuery({
    queryKey: ["documentForms", { document_id: documentId }],
    queryFn: () =>
      documentFormApi.list({
        queryKey: ["documentForms", { document_id: documentId }],
      }),
    enabled: isOpen && fieldValueSource === "user_input",
  });

  // Get all available form fields grouped by section
  const getAvailableFormFields = () => {
    if (!formData || formData.length === 0) return [];

    const currentForm = formData[0];
    const availableFields: {
      id: number;
      label: string;
      name: string;
      sectionTitle: string;
      stepTitle: string;
      stepOrder: number;
    }[] = [];

    if (currentForm.form_steps) {
      currentForm.form_steps.forEach((step) => {
        if (step.form_sections) {
          step.form_sections.forEach((section) => {
            if (section.form_fields) {
              section.form_fields.forEach((formField) => {
                if (formField.field_type !== "information") {
                  // Skip information fields
                  availableFields.push({
                    id: formField.id,
                    label: formField.label || `Field ${formField.id}`,
                    name:
                      formField.name ||
                      formField.label ||
                      `field_${formField.id}`,
                    sectionTitle:
                      section.section_title || `Section ${section.id}`,
                    stepTitle: step.step_title || `Step ${step.step_order + 1}`,
                    stepOrder: step.step_order,
                  });
                }
              });
            }
          });
        }
      });
    }

    // Sort by step order first, then by field name
    return availableFields.sort((a, b) => {
      if (a.stepOrder !== b.stepOrder) {
        return a.stepOrder - b.stepOrder;
      }
      return a.name.localeCompare(b.name);
    });
  };

  const availableFields = getAvailableFormFields();

  const handleSave = async () => {
    if (!fieldTitle.trim()) {
      toast({
        variant: "destructive",
        title: "Title is required",
        description: "Please enter a title for the field",
      });
      return;
    }

    if (fieldValueSource === "user_input" && !referencedFieldId) {
      toast({
        variant: "destructive",
        title: "Field reference required",
        description: "Please select a form field to reference",
      });
      return;
    }

    try {
      const fieldData: Partial<DocumentOutputField> = {
        title: fieldTitle.trim(),
        name: fieldName.trim() || undefined, // Only set if not empty
        value: fieldValueSource === "predefined" ? fieldValue : null,
        value_source: fieldValueSource,
        referenced_field_id:
          fieldValueSource === "user_input" ? referencedFieldId : undefined,
        comment: fieldComment.trim() || undefined, // Only set if not empty
      };

      await onSave(fieldData);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: `Error ${isEditMode ? "updating" : "adding"} field`,
        description:
          error.message ||
          `An error occurred while ${
            isEditMode ? "updating" : "adding"
          } the field`,
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit Field" : "Add Field"}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update the field properties."
              : "Add a new question/answer field to this subsection."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {/* Field Title */}
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="field-title" className="text-right">
              Title <span className="text-destructive">*</span>
            </label>
            <Input
              id="field-title"
              placeholder="Field title/question"
              value={fieldTitle}
              onChange={(e) => setFieldTitle(e.target.value)}
              className="col-span-3"
              disabled={isSubmitting}
            />
          </div>

          {/* Field Name */}
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="field-name" className="text-right">
              Name
            </label>
            <div className="col-span-3 space-y-1">
              <Input
                id="field-name"
                placeholder="field_name (optional)"
                value={fieldName}
                onChange={(e) => {
                  const value = e.target.value;
                  setFieldName(value);
                  // If user clears the field, resume auto-generation
                  setIsFieldNameManuallyEntered(value.trim() !== "");
                }}
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground">
                Optional. Auto-generated from title if not provided.
              </p>
            </div>
          </div>

          {/* Value Source */}
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="value-source" className="text-right">
              Value Source
            </label>
            <Select
              value={fieldValueSource}
              onValueChange={(value) => {
                setFieldValueSource(value as "predefined" | "user_input");
                if (value === "predefined") {
                  setReferencedFieldId(undefined);
                } else {
                  setFieldValue("");
                }
              }}
              disabled={isSubmitting}
            >
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Select value source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="predefined">Predefined</SelectItem>
                <SelectItem value="user_input">User Input</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {fieldValueSource === "predefined" && (
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="field-value" className="text-right">
                Value
              </label>
              <Input
                id="field-value"
                placeholder="Predefined value"
                value={fieldValue}
                onChange={(e) => setFieldValue(e.target.value)}
                className="col-span-3"
                disabled={isSubmitting}
              />
            </div>
          )}

          {fieldValueSource === "user_input" && (
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="referenced-field" className="text-right">
                Reference Field <span className="text-destructive">*</span>
              </label>
              <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    disabled={isSubmitting || availableFields.length === 0}
                    className={cn(
                      "col-span-3 justify-between w-full",
                      !referencedFieldId && "text-muted-foreground"
                    )}
                  >
                    {availableFields.length === 0
                      ? "No form fields available. Create a form first."
                      : referencedFieldId
                      ? availableFields.find((f) => f.id === referencedFieldId)
                          ?.name || "Unknown field"
                      : "Select a form field to reference..."}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0">
                  <Command
                    filter={(value, search) => {
                      // Custom filter function for precise search
                      if (!search) return 1;

                      const searchLower = search.toLowerCase();
                      const field = availableFields.find(
                        (f) => f.id.toString() === value
                      );
                      if (!field) return 0;

                      // Check if search matches field name, section title, or step title
                      const fieldName = field.name.toLowerCase();
                      const sectionTitle = field.sectionTitle.toLowerCase();
                      const stepTitle = field.stepTitle.toLowerCase();

                      // Exact word matching for better precision
                      const searchWords = searchLower
                        .split(" ")
                        .filter((word) => word.length > 0);

                      return searchWords.every(
                        (word) =>
                          fieldName.includes(word) ||
                          sectionTitle.includes(word) ||
                          stepTitle.includes(word)
                      )
                        ? 1
                        : 0;
                    }}
                  >
                    <CommandInput placeholder="Search by field name, section, or step..." />
                    <CommandList>
                      <CommandEmpty>No form field found.</CommandEmpty>
                      {(() => {
                        // Group fields by step
                        const fieldsByStep = availableFields.reduce(
                          (acc, field) => {
                            const stepKey = `${field.stepOrder}-${field.stepTitle}`;
                            if (!acc[stepKey]) {
                              acc[stepKey] = [];
                            }
                            acc[stepKey].push(field);
                            return acc;
                          },
                          {} as Record<string, typeof availableFields>
                        );

                        return Object.entries(fieldsByStep).map(
                          ([stepKey, stepFields]) => (
                            <CommandGroup
                              key={stepKey}
                              heading={stepFields[0].stepTitle}
                            >
                              {stepFields.map((formField) => (
                                <CommandItem
                                  key={formField.id}
                                  value={formField.id.toString()}
                                  onSelect={() => {
                                    setReferencedFieldId(formField.id);
                                    setComboboxOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      referencedFieldId === formField.id
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                  <div className="flex flex-col w-full">
                                    <span
                                      className="font-medium truncate"
                                      title={formField.name}
                                    >
                                      {formField.name}
                                    </span>
                                    <span
                                      className="text-xs text-muted-foreground truncate"
                                      title={`${formField.stepTitle} › ${formField.sectionTitle}`}
                                    >
                                      {formField.stepTitle} ›{" "}
                                      {formField.sectionTitle}
                                    </span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )
                        );
                      })()}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Comment */}
          <div className="grid grid-cols-4 items-start gap-4">
            <label htmlFor="field-comment" className="text-right mt-2">
              Comment
            </label>
            <div className="col-span-3 space-y-1">
              <Textarea
                id="field-comment"
                placeholder="Optional comment or description"
                value={fieldComment}
                onChange={(e) => setFieldComment(e.target.value)}
                className="resize-none h-20"
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground">
                Optional. Provide additional context or instructions for this
                field.
              </p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting
              ? "Saving..."
              : isEditMode
              ? "Update Field"
              : "Add Field"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentFormFieldDialog;
